import React, { useEffect, useRef, useState } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import avatarImg from "assets/img/team/40x40/avatar.webp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRankingStar, faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";

interface SalesTargetData {
  id: string;
  name: string;
  position: string;
  target: number;
  achieved: number;
  avatar?: string;
}

interface ImprovedSalesChartProps {
  data: SalesTargetData[];
  height?: number;
  onRefreshData?: () => void; // Função para atualizar os dados
}

const ImprovedSalesChart: React.FC<ImprovedSalesChartProps> = ({
  data,
  height = 600,
  onRefreshData,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Função para formatar valores monetários
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Ordenar dados por vendas realizadas (maior para menor)
  const sortedData = [...data].sort((a, b) => b.achieved - a.achieved);

  // Encontrar o valor máximo para normalizar as barras (usar apenas os valores máximos)
  const maxValue = Math.max(
    ...sortedData.map((item) => item.target),
    ...sortedData.map((item) => item.achieved)
  );

  // Função para alternar fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Função para iniciar o polling
  const startPolling = () => {
    if (onRefreshData && !pollIntervalRef.current) {
      setIsPolling(true);
      pollIntervalRef.current = setInterval(() => {
        onRefreshData();
      }, 60000); // 1 minuto
    }
  };

  // Função para parar o polling
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setIsPolling(false);
    }
  };

  // Effect para gerenciar o polling baseado no estado fullscreen
  useEffect(() => {
    if (isFullscreen && onRefreshData) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isFullscreen, onRefreshData]);

  // Effect para limpar o polling quando o componente é desmontado
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return (
    <Card
      className="h-100 border-0 shadow-sm"
      style={{
        borderRadius: "16px",
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        width: isFullscreen ? "100vw" : "auto",
        height: isFullscreen ? "100vh" : "auto",
        zIndex: isFullscreen ? 9999 : "auto", 
      }}
    >
      <Card.Header
        className="border-0 py-3"
        style={{
          borderRadius: "16px 16px 0 0",
          borderBottom: "1px solid var(--phoenix-tertiary-bg)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FontAwesomeIcon icon={faRankingStar} className="text-info" style={{ fontSize: '18px' }} />

            <div>
              <h6
                className="mb-1 text-body-emphasis fw-bold"
                style={{
                  fontSize: "16px",
                  fontFamily: "Inter, -apple-system, sans-serif",
                }}
              >
                Ranking de Performance
              </h6>
              <p
                className="mb-0 text-body-tertiary"
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                Classificação por vendas realizadas
                {isPolling && (
                  <span className="ms-2">
                    <span className="text-success">● </span>
                    Atualizando automaticamente
                  </span>
                )}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              className="bg-body-secondary border border-light-subtle"
              style={{
                borderRadius: "8px",
                padding: "6px 12px",
              }}
            >
              <span
                className="text-body-secondary"
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {data.length} vendedores
              </span>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Sair do modo tela cheia" : "Modo tela cheia"}
              style={{
                borderRadius: "8px",
                padding: "6px 12px",
                border: "1px solid var(--bs-border-color)",
              }}
            >
              <FontAwesomeIcon 
                icon={isFullscreen ? faCompress : faExpand} 
                style={{ fontSize: "14px" }}
              />
            </Button>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-4">
        {/* Verificar se há dados para exibir */}
        {data.length === 0 ? (
          <div 
            className="text-center d-flex flex-column align-items-center justify-content-center"
            style={{ height: `${isFullscreen ? window.innerHeight - 200 : height - 100}px` }}
          >
            <FontAwesomeIcon
              icon={faRankingStar}
              size="3x"
              className="text-muted mb-3"
            />
            <h5 className="text-muted">
              Nenhuma meta encontrada
            </h5>
            <p className="text-muted">
              Configure metas para a equipe para visualizar o
              ranking de performance.
            </p>
          </div>
        ) : (
          <>
            {/* Gráfico customizado */}
          <div
            style={{
              height: `${isFullscreen ? window.innerHeight - 200 : height - 100}px`,
              display: "flex",
              flexDirection: "column",
              gap: "24px",           
              overflowY: "auto",
              paddingRight: "8px",
            }}
          >
          {sortedData.map((person, index) => {
            const progress = (person.achieved / person.target) * 100;
            const achievedWidth = (person.achieved / maxValue) * 100;
            const targetWidth = (person.target / maxValue) * 100;
            const position = index + 1;
            const avatarSrc =
              person.avatar && person.avatar.startsWith("http")
                ? person.avatar
                : avatarImg;

            return (
              <div
                key={person.id}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: "16px",
                  animation: `slideInLeft 0.6s ease-out ${index * 0.1}s both`,
                  minHeight: "80px",
                  flexWrap: "wrap",
                }}
                className="sales-ranking-item"
              >
                {/* Avatar e informações do vendedor */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    minWidth: "220px",
                    flex: "0 0 220px",
                  }}
                  className="user-info-section"
                >
                  {/* Avatar */}
                  <div
                    style={{
                      position: "relative",
                      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.15))",
                    }}
                  >
                    <img
                      src={avatarSrc}
                      alt={person.name}
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: `3px solid ${
                          progress >= 100
                            ? "var(--bs-success)"
                            : progress >= 80
                            ? "var(--bs-warning)"
                            : "var(--bs-danger)"
                        }`,
                        backgroundColor: "white",
                        transition: "all 0.3s ease",
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = avatarImg;
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.1)";
                        e.currentTarget.style.filter =
                          "drop-shadow(0 8px 16px rgba(0,0,0,0.25))";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.filter =
                          "drop-shadow(0 4px 8px rgba(0,0,0,0.15))";
                      }}
                    />

                    {/* Badge de posição */}
                    <div
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        background:
                          position <= 3
                            ? position === 1
                              ? "var(--bs-warning)"
                              : position === 2
                              ? "var(--bs-secondary)"
                              : "var(--bs-warning)"
                            : "var(--bs-gray-600)",
                        color: "white",
                        borderRadius: "50%",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        border: "3px solid white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    >
                      {position <= 3
                        ? position === 1
                          ? "👑"
                          : position === 2
                          ? "🥈"
                          : "🥉"
                        : position}
                    </div>

                    {/* Indicador de meta atingida */}
                    {progress >= 100 && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-6px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: "var(--bs-success)",
                          color: "white",
                          borderRadius: "12px",
                          padding: "3px 8px",
                          fontSize: "11px",
                          fontWeight: "bold",
                          border: "2px solid white",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        🎯
                      </div>
                    )}
                  </div>

                  {/* Informações do vendedor */}
                  <div style={{ flex: 1 }}>
                    <h6
                      className="text-body-emphasis fw-bold"
                      style={{
                        marginBottom: "4px",
                        fontSize: "16px",
                        fontFamily: "Inter, -apple-system, sans-serif",
                      }}
                    >
                      {person.name}
                    </h6>
                    <div
                      className="text-body-tertiary"
                      style={{
                        fontSize: "13px",
                        marginBottom: "6px",
                        fontWeight: "500",
                      }}
                    >
                      {person.position}
                    </div>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background:
                          progress >= 100
                            ? "var(--bs-success-bg-subtle)"
                            : progress >= 80
                            ? "var(--bs-warning-bg-subtle)"
                            : "var(--bs-danger-bg-subtle)",
                        color:
                          progress >= 100
                            ? "var(--bs-success-text-emphasis)"
                            : progress >= 80
                            ? "var(--bs-warning-text-emphasis)"
                            : "var(--bs-danger-text-emphasis)",
                        padding: "3px 8px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {Math.round(progress)}% da meta {progress >= 100 && "🎯"}
                    </div>
                  </div>
                </div>

                {/* Área do gráfico */}
                <div
                  style={{
                    flex: "1 1 300px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    minWidth: "300px",
                  }}
                  className="chart-section"
                >
                  {/* Barra de Meta */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      className="text-body-tertiary"
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        minWidth: "35px",
                        fontFamily: "Inter, -apple-system, sans-serif",
                      }}
                    >
                      Meta
                    </span>
                    <div
                      style={{
                        flex: 1,
                        position: "relative",
                        height: "20px",
                        background: "var(--phoenix-tertiary-bg)",
                        borderRadius: "10px",
                        overflow: "hidden",                     
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "100%",
                          width: `${targetWidth}%`,
                          background:
                            "linear-gradient(90deg, #9ca3af 0%, #6b7280 100%)",
                          borderRadius: "10px",
                          transition: "width 1s ease-out",
                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
                        }}
                      />
                    </div>
                    <span
                      className="text-body-emphasis"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        minWidth: "60px",
                        textAlign: "right",
                        fontFamily: "Inter, -apple-system, sans-serif",
                      }}
                    >
                      {formatCurrency(person.target)}
                    </span>
                  </div>

                  {/* Barra de Vendas Realizadas */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      className="text-body-tertiary"
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        minWidth: "35px",
                        fontFamily: "Inter, -apple-system, sans-serif",
                      }}
                    >
                      Vendido
                    </span>
                    <div
                      style={{
                        flex: 1,
                        position: "relative",
                        height: "20px",
                        background: "var(--phoenix-tertiary-bg)",
                        borderRadius: "10px",
                        overflow: "hidden",
                    
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "100%",
                          width: `${achievedWidth}%`,
                          background:
                            person.achieved >= person.target
                              ? "linear-gradient(90deg, #10b981 0%, #059669 100%)" // Verde se atingiu meta
                              : "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)", // Laranja se não atingiu meta
                          borderRadius: "10px",
                          transition: "width 1s ease-out",
                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color:
                          person.achieved >= person.target
                            ? "#059669"
                            : "#d97706",
                        minWidth: "60px",
                        textAlign: "right",
                        fontFamily: "Inter, -apple-system, sans-serif",
                      }}
                    >
                      {formatCurrency(person.achieved)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CSS para animações */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
                        @keyframes slideInLeft {
                            from {
                                opacity: 0;
                                transform: translateX(-30px);
                            }
                            to {
                                opacity: 1;
                                transform: translateX(0);
                            }
                        }

                        /* Estilos para modo fullscreen */
                        .card {
                            transition: all 0.3s ease;
                        }

                        /* Scrollbar personalizada para modo fullscreen */
                        .card div::-webkit-scrollbar {
                            width: 8px;
                        }

                        .card div::-webkit-scrollbar-track {
                            background: var(--phoenix-tertiary-bg);
                            border-radius: 4px;
                        }

                        .card div::-webkit-scrollbar-thumb {
                            background: var(--bs-border-color);
                            border-radius: 4px;
                        }

                        .card div::-webkit-scrollbar-thumb:hover {
                            background: var(--bs-secondary);
                        }

                        /* Media queries para responsividade */
                        @media (max-width: 1200px) {
                            .sales-ranking-item {
                                flex-direction: column !important;
                                align-items: stretch !important;
                            }
                            
                            .user-info-section {
                                flex: 1 !important;
                                min-width: auto !important;
                                margin-bottom: 12px !important;
                                width: 100% !important;
                            }
                            
                            .chart-section {
                                flex: 1 !important;
                                min-width: auto !important;
                                width: 100% !important;
                            }
                        }

                        @media (max-width: 768px) {
                            .sales-ranking-item {
                                flex-direction: column !important;
                                align-items: stretch !important;
                                gap: 12px !important;
                            }
                            
                            .user-info-section {
                                flex: 1 !important;
                                min-width: auto !important;
                                margin-bottom: 8px !important;
                                gap: 12px !important;
                                width: 100% !important;
                            }
                            
                            .chart-section {
                                flex: 1 !important;
                                min-width: auto !important;
                                width: 100% !important;
                            }
                        }
                    `,
          }}
        />

        {/* Top 3 ranking clean */}
        {!isFullscreen && (
        <div
          className="mt-4 p-4 bg-body border"
          style={{
            borderRadius: "12px",
            margin: "20px",
          }}
        >
          <div
            style={{
              marginBottom: "20px",
            }}
          >
            <h6
              className="mb-0 text-body-emphasis"
              style={{
                fontWeight: "700",
                fontSize: "16px",
                fontFamily: "Inter, -apple-system, sans-serif",
                textAlign: "center",
              }}
            >
              Top 3 Vendedores
            </h6>
          </div>

          <Row className="g-3">
            {sortedData.slice(0, 3).map((person, index) => {
              const progress = (person.achieved / person.target) * 100;
              const avatarSrc =
                person.avatar && person.avatar.startsWith("http")
                  ? person.avatar
                  : avatarImg;

              return (
                <Col key={person.id} md={4}>
                  <div
                    className="bg-body-secondary border"
                    style={{
                      borderRadius: "16px",
                      padding: "20px",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 32px rgba(0,0,0,0.12)";
                      e.currentTarget.style.borderColor =
                        index === 0
                          ? "#fbbf24"
                          : index === 1
                          ? "#9ca3af"
                          : "#f59e0b";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0,0,0,0.06)";
                      e.currentTarget.style.borderColor = "var(--phoenix-tertiary-bg)";
                    }}
                  >
                    {/* Gradiente sutil de fundo baseado na posição */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          index === 0
                            ? "linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)"
                            : index === 1
                            ? "linear-gradient(90deg, #9ca3af 0%, #6b7280 100%)"
                            : "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)",
                        borderRadius: "16px 16px 0 0",
                      }}
                    />

                    {/* Avatar com efeito de sobreposição */}
                    <div
                      style={{
                        marginBottom: "16px",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          background: `linear-gradient(145deg, ${
                            index === 0
                              ? "#fef3c7, #fbbf24"
                              : index === 1
                              ? "#f3f4f6, #9ca3af"
                              : "#fed7aa, #f59e0b"
                          })`,
                          padding: "3px",
                          margin: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={avatarSrc}
                          alt={person.name}
                          style={{
                            width: "54px",
                            height: "54px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = avatarImg;
                          }}
                        />
                      </div>

                      {/* Badge de posição flutuante */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-8px",
                          right: "calc(50% - 30px - 12px)",
                          background:
                            index === 0
                              ? "#fbbf24"
                              : index === 1
                              ? "#9ca3af"
                              : "#f59e0b",
                          color: "white",
                          borderRadius: "12px",
                          padding: "4px 8px",
                          fontSize: "14px",
                          fontWeight: "700",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          border: "2px solid white",
                        }}
                      >
                        {index === 0 ? "👑" : index === 1 ? "🥈" : "🥉"}
                      </div>
                    </div>

                    {/* Informações com melhor hierarquia */}
                    <h6
                      className="text-body-emphasis"
                      style={{
                        fontWeight: "700",
                        marginBottom: "8px",
                        fontSize: "15px",
                        fontFamily: "Inter, -apple-system, sans-serif",
                        letterSpacing: "-0.025em",
                      }}
                    >
                      {person.name}
                    </h6>

                    {/* Métricas com destaque visual */}
                    <div
                      className="bg-body border-light-subtle"
                      style={{
                        marginBottom: "12px",
                        borderRadius: "12px",
                        padding: "12px",
                        border: "1px solid var(--phoenix-tertiary-bg)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "800",
                          color: progress >= 100 ? "#059669" : "var(--phoenix-body-emphasis-color)",
                          marginBottom: "4px",
                          fontFamily: "Inter, -apple-system, sans-serif",
                        }}
                      >
                        {formatCurrency(person.achieved)}
                      </div>

                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: progress >= 100 ? "#059669" : "var(--phoenix-body-tertiary-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        {progress >= 100 && <span>🎯</span>}
                        {Math.round(progress)}% da meta
                      </div>
                    </div>

                    {/* Indicador de progresso minimalista */}
                    <div
                      style={{
                        width: "100%",
                        height: "3px",
                        background: "var(--phoenix-tertiary-bg)",
                        borderRadius: "3px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(progress, 100)}%`,
                          height: "100%",
                          background:
                            progress >= 100
                              ? "linear-gradient(90deg, #10b981 0%, #059669 100%)"
                              : `linear-gradient(90deg, ${
                                  index === 0
                                    ? "#fbbf24, #f59e0b"
                                    : index === 1
                                    ? "#9ca3af, #6b7280"
                                    : "#f59e0b, #d97706"
                                })`,
                          borderRadius: "3px",
                          transition: "width 1s ease-out",
                        }}
                      />
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
        )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ImprovedSalesChart;
